import React, { useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { Button } from "components/ui";
import styles from "./dialog.module.scss";
import Input from "./Input";
import { useMutation } from "@apollo/client";
import appInfo from "utility/app-info";
import { MUTATION_ADD_COMPANY_DOMAIN } from "apps/company/api/graphQL/mutation";
import { isValidDomain } from "apps/company/common/utils";
import { CommonModalProps } from "./common";
import { logError } from "utility/Function";
import { useTranslation } from "lib/i18n/use-translation";

interface AddDomainDialogProps extends CommonModalProps {}

const AddDomainDialog = ({
  title,
  description,
  action,
  isOpen,
  onCloseAction,
  onAction,
}: AddDomainDialogProps) => {
  //HOOKS
  const { t } = useTranslation(["admin"]);
  const [domain, setDomain] = useState<string>("");
  const [addDomain] = useMutation(MUTATION_ADD_COMPANY_DOMAIN);
  const [errorText, setErrorText] = useState<string>("");

  //HANDLER
  const toggle = () => {
    reset();
    onCloseAction();
  };
  const reset = () => {
    setDomain("");
    setErrorText("");
  };

  const handleAction = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isValidDomain(domain)) {
      setErrorText("");
      addDomain({
        variables: {
          appInfo,
          domain,
        },
      })
        .then((response) => {
          if (response.errors) {
            setErrorText(response.errors[0].message);
          } else if (response.data) {
            toggle();
            onAction();
            reset();
          }
        })
        .catch((error) => {
          setErrorText(error.message);
          logError("Error adding domain", error);
        });
    } else {
      setErrorText(t("admin:integrations.addDomain.invalidDomain"));
    }
  };
  return (
    <Modal
      isOpen={isOpen}
      style={{ width: "100%", maxWidth: "440px" }}
      className={styles["modal"]}
      contentClassName={styles["modal-content"]}
      centered
      data-testid="add-domain-modal"
    >
      <form aria-label="add-domain-form" onSubmit={handleAction}>
        <ModalHeader className={styles["header"]} toggle={toggle}></ModalHeader>

        <ModalBody
          className={`${styles["body"]}`}
          data-testid="add-domain-modal-body"
        >
          <p className={styles["title"]}>{title}</p>
          <p className={`text-small ${styles["description"]}`}>{description}</p>
          <div className={styles["inputs"]}>
            <Input
              value={domain}
              label={t("admin:integrations.addDomain.domainLabel")}
              onChange={(e) => setDomain(e.target.value.toLowerCase())}
              type="text"
              placeholder={t("admin:integrations.addDomain.domainPlaceholder")}
              required
            />
            {errorText && (
              <p data-testid="error-text" className={styles["error"]}>
                {errorText}
              </p>
            )}
          </div>
        </ModalBody>
        <ModalFooter
          className={styles["footer"]}
          data-testid="add-domain-modal-footer"
        >
          <Button variant="primary" className="sans" type="submit">
            {action}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default AddDomainDialog;
